import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Link } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { get } from 'lodash';
import { LinkButton, LinkButtonProps, useEntity, useEntityPathDeriver } from '@shapeable/ui';
import { ChevronRightIconGlyph, ChevronRightThinIconGlyph } from '@shapeable/icons';

const cls = classNames('link-entity-button');

// -------- Types -------->

export type LinkEntityButtonProps = Classable & HasChildren & Omit<LinkButtonProps, 'href'> & { 
  entity: Link;
  showTypeAsSubLabel?: boolean;
};

export const LinkEntityButtonDefaultProps: Omit<LinkEntityButtonProps, 'entity'> = {
  showTypeAsSubLabel: true,
};

// -------- Child Component Props -------->

type ContainerProps = {
  _backgroundColor?: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
    base: css`
      max-width: fit-content;
  
      .shp--button {
        position: relative;
        overflow: hidden;
        isolation: isolate;

        ${({ _backgroundColor }: ContainerProps) => _backgroundColor && css`
          background: ${theme.COLOR(_backgroundColor)};
          border: none;
        `}
        color: ${theme.COLOR('light')};
        border-radius: ${theme.UNIT(8)};
        font-size: 0.8em;
        font-weight: 600;
        padding: 12px 24px;
        ${theme.TRANSITION('transform', 'box-shadow', 'filter')};

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            transparent 40%,
            rgba(255, 255, 255, 0.18) 50%,
            transparent 60%
          );
          transform: translateX(-120%);
          transition: transform 0.55s ease;
          pointer-events: none;
        }

        .shp--icon {
          ${theme.TRANSITION('transform')};
        }
        
        &:hover {
          filter: brightness(1.06);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);

          &::before {
            transform: translateX(120%);
          }

          .shp--icon {
            transform: translateX(4px);
          }
        }

        &:focus-visible {
          outline: 2px solid rgba(255, 255, 255, 0.55);
          outline-offset: 2px;
        }
        
        &:active {
          transform: translateY(0) scale(0.98);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          filter: brightness(0.98);
        }

        @media (prefers-reduced-motion: reduce) {
          &::before {
            display: none;
          }

          &:hover {
            transform: none;

            .shp--icon {
              transform: none;
            }
          }

          &:active {
            transform: none;
          }
        }
      }
    `,
  });


// -------- Components -------->

const My = {
  Container: styled(LinkButton)<ContainerProps>`${ContainerStyles}`,
};

export const LinkEntityButton: Shapeable.FC<LinkEntityButtonProps> = (props) => {
  const { className, children, showTypeAsSubLabel, ...rest } = props;
  const entity = useEntity(props.entity);
  
  const getEntityPath = useEntityPathDeriver();

  const { color } = entity;

  const pagePath = entity.page && getEntityPath(entity.page);
  const fileUrl = get(entity, 'file.file.url');
  const url = get(entity, 'url');
  const isDownload = rest.isDownload || !!entity.isDownload || (!pagePath && !!fileUrl);
  const href = pagePath || fileUrl || url;
  const colorValue = color?.value || 'primary';

  const label = children || entity.label || entity.name;

  const type = get(entity, 'type.name');

  const subLabel = showTypeAsSubLabel && type;

  return (
   <My.Container 
    href={href} 
    {...rest} 
    subLabel={subLabel} 
    isDownload={isDownload} 
    className={cls.name(className)}
    _backgroundColor={colorValue}
    icon={ChevronRightThinIconGlyph}
    iconSize={17}
    colors={{ fill: 'light' }}
    >
   {label}
   </My.Container>
  )
};

LinkEntityButton.defaultProps = LinkEntityButtonDefaultProps;
LinkEntityButton.cls = cls;